import { createFileRoute, notFound } from "@tanstack/react-router";
import { ensureProposalCollections } from "~/api/proposal-collections/queries";
import { CollectionNotFoundComponent } from "~/routes/(restricted)/byggeradar/-components/CollectionNotFoundComponent";
import { CollectionPage } from "~/routes/(restricted)/byggeradar/-components/CollectionPage";
import { useByggeRadarTracking } from "~/routes/(restricted)/byggeradar/-hooks/useByggeRadarTracking";
import { useProposalCollection } from "~/routes/(restricted)/byggeradar/-hooks/useProposalCollection";
import { useTrackOncePer } from "~/routes/(restricted)/byggeradar/-hooks/useTrackOncePer";
import { proposalSearchSchema } from "~/routes/(restricted)/byggeradar/-schemas/proposal-search";
import { collectionTrackingProperties } from "~/routes/(restricted)/byggeradar/-utils/tracking";

export const Route = createFileRoute(
  "/(restricted)/byggeradar/_access/collection/$collectionId",
)({
  component: CollectionComponent,
  validateSearch: proposalSearchSchema,
  beforeLoad: async ({
    params: { collectionId },
    context: { queryClient },
  }) => {
    const collections = await ensureProposalCollections(queryClient);
    const preloadedCollection =
      collections.find((collection) => collection.id === collectionId) ?? null;

    return { preloadedCollection };
  },
  loader: async ({ context: { preloadedCollection, i18n } }) => {
    if (!preloadedCollection) {
      throw notFound();
    }

    await i18n.loadNamespaces("proposals");

    const title = i18n.t("proposals:collection.meta.title", {
      collectionName: preloadedCollection.name,
    });

    return { meta: { title } };
  },
  head: async (context) => {
    return {
      meta: [
        {
          title: context.loaderData?.meta.title,
        },
      ],
    };
  },
  notFoundComponent: CollectionNotFoundComponent,
});

function CollectionComponent() {
  const search = Route.useSearch();
  const { collectionId } = Route.useParams();

  const { trackEvent } = useByggeRadarTracking();
  const { proposalCollection } = useProposalCollection(collectionId);
  useTrackOncePer(proposalCollection?.id, () => {
    if (proposalCollection) {
      trackEvent(
        "collection_opened",
        collectionTrackingProperties(proposalCollection),
      );
    }
  });

  return (
    <CollectionPage
      collectionId={collectionId}
      search={search}
    />
  );
}
